import { type TaskCreateDto, type TaskPriority, type TaskStatus } from "@crowdcoursing/api/openapi";
import { Flag } from "@mui/icons-material";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { DateTime } from "luxon";
import { Field, Form } from "react-final-form";

import { CharacterLimitedTextField as TextField } from "~ui/atoms/CharacterLimitedTextField/CharacterLimitedTextField";

import { type TaskAssigneeOption } from "./taskAssignees";

const TASK_PRIORITIES: { label: string; value: TaskPriority }[] = [
  { label: "Urgent", value: 1 },
  { label: "High", value: 2 },
  { label: "Medium", value: 3 },
  { label: "Low", value: 4 },
];

const TASK_STATUSES: { label: string; value: TaskStatus }[] = [
  { label: "To do", value: 1 },
  { label: "In Progress", value: 2 },
  { label: "Blocked", value: 3 },
  { label: "Done", value: 4 },
];

interface TaskFormValues extends TaskCreateDto {
  dueAt: string | null;
  reminderAt: string | null;
}

interface TaskFormProps {
  assigneeOptions?: TaskAssigneeOption[];
  /** Used when the task does not already have an assignee. */
  defaultAssigneeId?: string;
  initialValues?: Partial<TaskFormValues>;
  onSubmit: (values: TaskFormValues) => void;
}

const tAssignedTo = "Assigned to";

export const TaskForm = ({ assigneeOptions, defaultAssigneeId, initialValues, onSubmit }: TaskFormProps) => {
  return (
    <Form<TaskFormValues>
      initialValues={{
        assignedToUserId: defaultAssigneeId,
        // Todo
        priority: 4,
        status: 1, // Low
        ...initialValues,
      }}
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <form id="task-form" onSubmit={handleSubmit}>
          <Box display="grid" gap={3}>
            {/* Title */}
            <Field name="title">
              {({ input }) => (
                <TextField
                  {...input}
                  inputProps={{ maxLength: 240 }}
                  label="Title"
                  required
                  fullWidth
                  placeholder="What needs to be done?"
                />
              )}
            </Field>

            {/* Description */}
            <Field name="description">
              {({ input }) => (
                <TextField
                  {...input}
                  inputProps={{ maxLength: 4000 }}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional details..."
                />
              )}
            </Field>

            {assigneeOptions && (
              <Field name="assignedToUserId">
                {({ input }) => (
                  <FormControl fullWidth>
                    <InputLabel id="task-assignee-label">{tAssignedTo}</InputLabel>
                    <Select
                      {...input}
                      data-testid="task-assignee"
                      label={tAssignedTo}
                      labelId="task-assignee-label"
                      value={input.value ?? ""}
                    >
                      {assigneeOptions.map((assignee) => (
                        <MenuItem key={assignee.id} disabled={assignee.disabled} value={assignee.id}>
                          {assignee.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Field>
            )}

            {/* Priority and Status */}
            <Box display="grid" gap={2} gridTemplateColumns="1fr 1fr">
              <Field name="priority">
                {({ input }) => (
                  <FormControl fullWidth>
                    <InputLabel id="priority-label">Priority</InputLabel>
                    <Select {...input} labelId="priority-label" label="Priority">
                      {TASK_PRIORITIES.map((priority) => (
                        <MenuItem key={priority.value} value={priority.value}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Flag
                              sx={{
                                color:
                                  priority.value === 1
                                    ? "#d32f2f" // Urgent - Deeper red
                                    : priority.value === 2
                                      ? "#f57c00" // High - Orange
                                      : priority.value === 3
                                        ? "warning.main" // Medium - Yellow
                                        : "primary.main", // Low
                              }}
                              fontSize="small"
                            />
                            {priority.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Field>

              <Field name="status">
                {({ input }) => (
                  <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select {...input} labelId="status-label" label="Status">
                      {TASK_STATUSES.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Field>
            </Box>

            {/* Due Date and Reminder */}
            <Box display="grid" gap={2} gridTemplateColumns="1fr 1fr">
              <Field name="dueAt">
                {({ input }) => (
                  <TextField
                    label="Due Date"
                    type="datetime-local"
                    value={input.value ? DateTime.fromISO(input.value).toFormat("yyyy-MM-dd'T'HH:mm") : ""}
                    onChange={(e) => {
                      const date = e.target.value ? DateTime.fromISO(e.target.value).toISO() : null;
                      input.onChange(date);
                    }}
                    fullWidth
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                )}
              </Field>

              <Field name="reminderAt">
                {({ input }) => (
                  <TextField
                    label="Reminder"
                    type="datetime-local"
                    value={input.value ? DateTime.fromISO(input.value).toFormat("yyyy-MM-dd'T'HH:mm") : ""}
                    onChange={(e) => {
                      const date = e.target.value ? DateTime.fromISO(e.target.value).toISO() : null;
                      input.onChange(date);
                    }}
                    fullWidth
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                )}
              </Field>
            </Box>
          </Box>
        </form>
      )}
    />
  );
};
